import React from 'react';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Radio } from 'src/design/atoms/Radio';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import {
  DocumentDto,
  FolderRequestDto,
  JobUserDto,
  SharedWithUserRequest,
  UserDto,
} from 'src/api/FiretrolApiClient';
import {
  useCreateFolder,
  useUpdateFolder,
} from 'src/api/queries/documents/useFolderMutations';
import { useJobs } from 'src/hooks/useJobs';
import { JobUserSelect } from 'src/routes/Jobs/Documents/components/FolderDialog/JobUserSelect';
import {
  DOCUMENT_ACCESS,
  DOCUMENT_FOLDER_TYPE,
  DocumentAccess,
  DocumentFolderType,
  ROOT_FOLDER_ID,
} from 'src/app/documents/documentConstants';
import {
  FIELD_LIMITS,
  getErrorMessage,
  showSuccessToast,
  toFormikFieldErrors,
} from 'src/utils';
import { Modal } from 'src/design/molecules/Modal';
import { BasicButton } from 'src/design/atoms/BasicButton';
interface IFolderDialogProps {
  isOpen: boolean;
  isLocked: boolean;
  currentFolderId: string | null;
  onClose: () => void;
  parentFolder: DocumentDto | null;
  masterId: string;
  edit?: boolean;
}

export const FolderDialog: React.FC<IFolderDialogProps> = ({
  isOpen,
  isLocked,
  masterId,
  edit,
  parentFolder,
  onClose,
  currentFolderId,
}) => {
  const { selectedJob } = useJobs();
  const createFolder = useCreateFolder(selectedJob?.id);
  const updateFolder = useUpdateFolder(selectedJob?.id);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {},
  );
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [sharedWith, setSharedWith] = React.useState<JobUserDto[]>([]);
  const [access, setAccess] = React.useState<DocumentAccess>(
    DOCUMENT_ACCESS.all,
  );
  const [folderType, setFolderType] = React.useState<DocumentFolderType>(
    DOCUMENT_FOLDER_TYPE.standard,
  );

  const isNew = !edit;
  const isRootFolder =
    currentFolderId === ROOT_FOLDER_ID ||
    (!isNew && parentFolder && parentFolder.parentId === null);
  const isSubFolder = currentFolderId !== ROOT_FOLDER_ID;
  const enableOptions = isRootFolder || (!isNew && !isSubFolder);

  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else if (isOpen && parentFolder !== null) {
      setEmail(parentFolder.email ?? '');
      setFolderType(
        (parentFolder.folderType as DocumentFolderType) ??
          DOCUMENT_FOLDER_TYPE.standard,
      );
      setAccess((parentFolder.access as DocumentAccess) ?? DOCUMENT_ACCESS.all);
      const mappedUsers: JobUserDto[] = (parentFolder.sharedWith ?? [])
        .filter((sw): sw is typeof sw & { user: UserDto } => sw.user != null)
        .map((sw) => ({
          id: sw.jobUserId ?? sw.id,
          userId: sw.userId,
          source: 'manual',
          user: sw.user,
        }));
      setSharedWith(mappedUsers);
      if (edit) {
        setName(parentFolder.name ?? '');
      }
    }
  }, [isOpen]);

  const resetForm = () => {
    setFieldErrors({});
    setName('');
    setEmail('');
    setSharedWith([]);
    setAccess(DOCUMENT_ACCESS.all);
    setFolderType(DOCUMENT_FOLDER_TYPE.standard);
  };
  const saveFolder = async () => {
    if (!selectedJob) return;
    setError(null);
    setFieldErrors({});
    try {
      const sharedWithRequest: SharedWithUserRequest[] = sharedWith.map(
        (u) => ({
          id: u.id,
          userId: u.userId,
          jobUserId: u.id,
        }),
      );

      if (edit) {
        if (!parentFolder) return;
        const request: FolderRequestDto = {
          parentId: parentFolder.parentId ?? undefined,
          masterId: masterId === ROOT_FOLDER_ID ? undefined : masterId,
          name,
          email,
          folderType,
          access,
          sharedWith: sharedWithRequest,
        };
        await updateFolder.mutateAsync({
          folderId: parentFolder.id,
          request,
        });
        showSuccessToast(`"${name}" updated!`);
        onClose();
      } else {
        const request: FolderRequestDto = {
          parentId:
            currentFolderId === ROOT_FOLDER_ID
              ? undefined
              : currentFolderId ?? undefined,
          masterId: masterId === ROOT_FOLDER_ID ? undefined : masterId,
          name,
          email,
          folderType,
          access,
          sharedWith: sharedWithRequest,
        };
        await createFolder.mutateAsync(request);
        showSuccessToast(`"${name}" created!`);
        onClose();
      }
    } catch (err: unknown) {
      setFieldErrors(toFormikFieldErrors(err));
      setError(getErrorMessage(err));
    }
  };
  const tooLong = (value: string, label: string) =>
    value.length > FIELD_LIMITS.singleLine
      ? `${label} must be ${FIELD_LIMITS.singleLine} characters or fewer`
      : undefined;

  const nameTooLong = tooLong(name, 'Name');
  const emailTooLong = tooLong(email, 'Email');
  const nameError = nameTooLong ?? fieldErrors.name;
  const emailError = emailTooLong ?? fieldErrors.email;
  const canSave =
    name.length >= 3 &&
    !nameTooLong &&
    (folderType !== DOCUMENT_FOLDER_TYPE.emails || !emailTooLong);

  const clearFieldError = (field: 'name' | 'email') =>
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSave) {
      saveFolder();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={edit ? 'Edit Folder' : 'New Folder'}
      actions={
        isLocked
          ? []
          : [
              <BasicButton
                key="save"
                onClick={saveFolder}
                loading={createFolder.isPending || updateFolder.isPending}
                disabled={!canSave}
              >
                {edit ? 'Save Changes' : 'Create Folder'}
              </BasicButton>,
            ]
      }
    >
      {isLocked ? (
        <Stack gap="md">
          <Alert color="blue">
            This folder is system generated and cannot be modified
          </Alert>
          <TextInput
            label="Name"
            autoFocus
            autoComplete="off"
            disabled
            placeholder="Folder name..."
            value={name}
          />
        </Stack>
      ) : (
        <Stack gap="md">
          {!isRootFolder && (
            <Alert color="blue">
              Subfolders share the same settings as their parent
            </Alert>
          )}
          <TextInput
            label="Name"
            withAsterisk
            description="At least 3 characters"
            autoFocus
            onKeyDown={handleKeyDown}
            autoComplete="off"
            placeholder="Folder name..."
            value={name}
            maxLength={FIELD_LIMITS.singleLine}
            error={nameError}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
          />
          <Stack gap="xs">
            <Radio.Group
              label="Folder Type"
              value={folderType}
              error={fieldErrors.folderType}
              onChange={(value) => setFolderType(value as DocumentFolderType)}
            >
              <Group>
                <Radio
                  label="Standard"
                  value={DOCUMENT_FOLDER_TYPE.standard}
                  disabled={!enableOptions}
                />
                <Radio
                  label="Email Inbox"
                  value={DOCUMENT_FOLDER_TYPE.emails}
                  disabled={!enableOptions}
                />
              </Group>
            </Radio.Group>
          </Stack>
          {folderType === DOCUMENT_FOLDER_TYPE.emails && (
            <TextInput
              disabled={!enableOptions}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              label="Email"
              placeholder="Enter email..."
              value={email}
              maxLength={FIELD_LIMITS.singleLine}
              error={emailError}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
            />
          )}
          <Stack gap="xs">
            <Radio.Group
              label="Who can access"
              value={access}
              error={fieldErrors.access}
              onChange={(value) => setAccess(value as DocumentAccess)}
            >
              <Stack gap="xs">
                <Radio
                  label="Anyone with access to this job"
                  description="Everyone assigned to the job can open this folder"
                  value={DOCUMENT_ACCESS.all}
                  disabled={!enableOptions}
                />
                <Radio
                  label="Only you"
                  description="Hidden from everyone else on the job"
                  value={DOCUMENT_ACCESS.onlyYou}
                  disabled={!enableOptions}
                />
                <Radio
                  label="Specific people"
                  description="Only the people you pick can open this folder"
                  value={DOCUMENT_ACCESS.people}
                  disabled={!enableOptions}
                />
              </Stack>
            </Radio.Group>
          </Stack>
          {access === DOCUMENT_ACCESS.people && (
            <Stack gap="xs">
              <Text size="sm" fw={500} c="bright">
                Select People
              </Text>
              <Input.Wrapper error={fieldErrors.sharedWith}>
                <JobUserSelect
                  disabled={!enableOptions}
                  hideLabel
                  value={sharedWith}
                  onChange={(users) => {
                    if (users) {
                      setSharedWith(users);
                    } else {
                      setSharedWith([]);
                    }
                  }}
                />
              </Input.Wrapper>
            </Stack>
          )}
        </Stack>
      )}

      {error && <Text c="red">{error}</Text>}
    </Modal>
  );
};
